import React, {
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback
} from 'react'
import styled from 'styled-components'
import { GridItem } from '@zuto/core/src/grid'
import { RadioButton } from '@zuto/atoms'
import * as yup from 'yup'
import MuiErrorMessage from '@zuto/atoms/src/Mui/ErrorMessage/MuiErrorMessage'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import debounce from 'lodash.debounce'
import AppFormQuestion from '../../AppFormQuestion'
import QuestionKey from '../../../Questions/question-key'
import TitleOptions from '../TitleQuestion/TitleOptions'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import QuestionComponentProps from '../../../Questions/question-component-props'
import {
  trackMultipleInputChoice, handlePrevButtonClick, questionFontStyle, determineRadioVariant, fireReactGAEvent
} from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import MobileNumberValidator from '../../../validators/MobileNumberValidator'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { EmailAddressValidatorLookupService, displayClickableEmail } from '../../../validators/EmailAddressValidatorLookupService'
import { AttributionContext } from '../../../context'
import { Loading } from '@zuto/molecules'
import getConfig from 'next/config'
import LookupService from '../../../services/lookup-service'
import ToggleButtonGroup from '@zuto/atoms/src/Mui/ToogleButtonGroup/ToggleButtonGroup'
import { firstNameSchema, lastNameSchema } from '../../../validators/schemaBuilders/nameFields'
import EmailAddressValidator from '../../../validators/EmailAddressValidator'

const fieldValues = {
  title: '',
  firstName: '',
  surname: '',
  contactNumber: '',
  emailAddress: ''
}

type FieldKey = keyof typeof fieldValues
type ValidationSchemaKey = 'firstName' | 'surname' | 'contactNumber' | 'emailAddress'


type TitleSelectorWrapperProps = {
  isError: boolean
}

const TitleSelectorWrapper = styled.div<TitleSelectorWrapperProps>`
  ${(props) => (props.isError ? 'margin-bottom: 10px;' : '')}
  margin-bottom: 22px; 
  position: relative;
`

export const GroupedQuestionsForReorderingCaseV2 = ({
  existingAnswers,
  disablePrevious,
  errors: existingErrors,
  bouncerError,
  updateFollowingPrefillStep
}: QuestionComponentProps): JSX.Element => {
  const { setQuestionName, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const { dispatch, featureSwitches } = useContext(ApplicationFormContext)
  const attribution = useContext(AttributionContext)
  const { publicRuntimeConfig } = getConfig()
  const lookupService = LookupService(publicRuntimeConfig)
  const {
    reviewPageV3,
    questionCopyChangeV1,
    bouncerV1,
    msmThemeV1
  } = featureSwitches ?? {}
  const determinedFont = questionFontStyle
  const [loading, setLoading] = useState(false)
  const [focus, setFocus] = useState<FieldKey | 'nextButton' | 'previousButton' | null>(null)

  // Cast and normalize upfront so all inputs always receive strings never null or undefined
  const normalisedInitialValues: { [key in FieldKey]: string } = {
    title: existingAnswers?.title ?? '',
    firstName: String(firstNameSchema.cast(existingAnswers?.firstName?.trim()  ?? '')),
    surname: String(lastNameSchema.cast(existingAnswers?.surname?.trim()  ?? '')),
    contactNumber: existingAnswers?.contactNumber ?? '',
    emailAddress: existingAnswers?.emailAddress ?? ''
  }

  const [values, setValues] = useState<{ [key in FieldKey]: string }>(normalisedInitialValues)

  const [showErrors, setShowErrors] = useState<{ [key in FieldKey]: boolean }>({
    title: false,
    firstName: false,
    surname: false,
    contactNumber: false,
    emailAddress: false
  })

  const [dirtyFields, setDirtyFields] = useState<{ [key in FieldKey]: boolean }>({
    title: !!existingAnswers?.title,
    firstName: !!existingAnswers?.firstName,
    surname: !!existingAnswers?.surname,
    contactNumber: !!existingAnswers?.contactNumber,
    emailAddress: !!existingAnswers?.emailAddress
  })

  const [emailValidationError, setEmailValidationError] = useState<string>(bouncerError ?? '')

  const firstNameValidator = yup.object().shape({ firstName: firstNameSchema })

  const lastNameValidator = yup.object().shape({ surname: lastNameSchema })

  // This is pretty lazy typing but yup types kinda suck.
  type Validator =
    | typeof firstNameValidator
    | typeof lastNameValidator

  const validationSchemas: {
    [key in ValidationSchemaKey]: Validator | yup.BaseSchema
  } = {
    firstName: firstNameValidator,
    surname: lastNameValidator,
    contactNumber: MobileNumberValidator().getValidator(),
    emailAddress: bouncerV1 ? EmailAddressValidatorLookupService(emailValidationError).getValidator() : EmailAddressValidator().getValidator()
  }

  const validate = (key: ValidationSchemaKey, value: string): string => {
    try {
      validationSchemas[key].validateSync({ [key]: value })
    } catch (untypedError: unknown) {
      const errorObject = untypedError as yup.ValidationError
      return errorObject.message
    }
    return ''
  }

  const hasUserTypedValue = Object.entries(values)
    .filter(([key]) => key !== 'title')
    .some(([, value]) => !!value)

  const titleError = useMemo(() => {
    const isCurrentError = !values.title && !!focus && focus !== 'title'
    const isExistingError = !values.title && hasUserTypedValue && !!existingErrors?.title

    return isCurrentError || isExistingError ? 'Please select a title' : ''
  }, [values.title, focus, hasUserTypedValue])
  
  const firstNameError = useMemo(() => validate('firstName', values.firstName), [values.firstName])
  const surnameError = useMemo(() => validate('surname', values.surname), [values.surname])
  const contactNumberError = useMemo(() => validate('contactNumber', values.contactNumber), [values.contactNumber])
  const emailAddressError = useMemo(() => validate('emailAddress', values.emailAddress), [values.emailAddress, emailValidationError])

  const errors = [titleError, firstNameError, surnameError, contactNumberError, emailAddressError]


  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('personalDetails')
    }
  }, [])

  const debouncedSetShowError = (key: FieldKey) => {
    setShowErrors((previous) => ({ ...previous, [key]: true }))
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Validation Error',
      label: key,
      nonInteraction: true
    })
  }

  const debounceMap: Record<FieldKey, () => void> = useMemo(
    () => ({
      title: debounce(() => debouncedSetShowError('title'), 1500),
      firstName: debounce(() => debouncedSetShowError('firstName'), 1500),
      surname: debounce(() => debouncedSetShowError('surname'), 1500),
      contactNumber: debounce(() => debouncedSetShowError('contactNumber'), 1500),
      emailAddress: debounce(() => debouncedSetShowError('emailAddress'), 1500)
    }),
    []
  )

  const setShouldShowError = useCallback((key: FieldKey, errorMessage: string, isDirty: boolean, showError: boolean): void => {
    if (isDirty && errorMessage) {
      if (showError) {
        setShowErrors((previous) => ({ ...previous, [key]: true }))
      } else {
        // We'll only debounce if no error is currently displayed.
        debounceMap[key]()
      }
    } else {
      setShowErrors((previous) => ({ ...previous, [key]: false }))
    }
  }, [])

  useEffect(
    () => setShouldShowError(
      'title',
      titleError,
      true, // We'll pass `isDirty` always as true because the validation of the title field only applies when the field is clean.
      showErrors.title
      || (!!existingErrors?.title && hasUserTypedValue)
    ),
    // We're passing `focus` to the dependency list so that this side effect will run every time the user changes the focussed field.
    [titleError, dirtyFields.title, showErrors.title, focus]
  )
  useEffect(
    () => setShouldShowError('firstName', firstNameError, dirtyFields.firstName, showErrors.firstName),
    [values.firstName, firstNameError, dirtyFields.firstName, showErrors.firstName]
  )
  useEffect(
    () => setShouldShowError('surname', surnameError, dirtyFields.surname, showErrors.surname),
    [values.surname, surnameError, dirtyFields.surname, showErrors.surname]
  )
  useEffect(
    () => setShouldShowError(
      'contactNumber',
      contactNumberError,
      dirtyFields.contactNumber,
      showErrors.contactNumber
      || (!!existingErrors?.contactNumber && !!existingAnswers.contactNumber)
    ),
    [values.contactNumber, contactNumberError, dirtyFields.contactNumber, showErrors.contactNumber]
  )
  useEffect(
    () => setShouldShowError(
      'emailAddress',
      emailAddressError || emailValidationError,
      dirtyFields.emailAddress,
      showErrors.emailAddress || !!emailValidationError
      || (!!existingErrors?.emailAddress && !!existingAnswers?.emailAddress)
    ),
    [values.emailAddress, emailAddressError, dirtyFields.emailAddress, showErrors.emailAddress]
  )

  const displayNextButton: boolean = useMemo(
    () => Object.values(dirtyFields).every((isDirty) => isDirty) && errors.every((error) => !error),
    [dirtyFields, errors]
  )

  const canDisablePrevious = disablePrevious ? 'invisible' : undefined
  const canDisablePreviousForReviewFeature = reviewPageV3 ? !displayNextButton : canDisablePrevious

  const onChange = useCallback((key: FieldKey, value: string) => {
    if (key === 'emailAddress' && bouncerV1) setEmailValidationError('')
    
    const nextValue = key === 'firstName' || key === 'surname'
      ? String( key === 'firstName' ? firstNameSchema.cast(value) : lastNameSchema.cast(value))
      : value
    
    // if the field is already dirty, do nothing otherwise mark it dirty exactly once
    setDirtyFields(previous => previous[key] ? previous : { ...previous, [key]: true })
    
    setValues((previous) => ({ ...previous, [key]: nextValue }))
  }, [])

  const runDisplayClickableEmail = useCallback(
    (emailAddressError: string) => displayClickableEmail(emailAddressError, onChange)
    , [onChange])

  const dispatchQuestionAnswered = () => {
    setEmailValidationError('')
    trackMultipleInputChoice({ label: `title+${values.title}` })
    dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.PERSONAL_DETAILS,
      value: values,
      errors: showErrors,
      ...(bouncerV1 && { bouncerError: '' })
    })
    // Used for Navigation on Prefill Review Page
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, false, true)
      }
    }
  }

  const nextQuestion = async () => {
    if (displayNextButton) {
      if (!bouncerV1) return dispatchQuestionAnswered()
      try {
        setLoading(true)
        await lookupService.validateEmail(values.emailAddress, attribution.source)
        dispatchQuestionAnswered()
      } catch (err) {
        const error = err as { message: string }
        setLoading(false)
        setEmailValidationError(error?.message)
        !(error?.message) && dispatchQuestionAnswered()
      }
    }
  }

  const previousQuestion = () => {
    handlePrevButtonClick(dispatch, {
      name: QuestionKey.PERSONAL_DETAILS,
      value: values,
      errors: showErrors,
      ...(bouncerV1 && { bouncerError: emailValidationError })
    })
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(false, false, true)
      }
    }
  }

  const handleEnterPress = (event: React.KeyboardEvent): void => {
    if (event.code === 'Enter' || event.key === 'Enter') {
      nextQuestion()
    }
  }

  const formattedTitleError = <MuiErrorMessage error={titleError} isGroupedOutlined />

  const renderOptions = () => {
    if (msmThemeV1) {
      return (
        <TitleSelectorWrapper isError={!!titleError}>
          <ToggleButtonGroup
            options={TitleOptions}
            id="title"
            oneLine={true}
            data-testid="title-options"
            defaultState={values?.title}
            valueChanged={(value) => onChange('title', value)}
          />
          {formattedTitleError}
        </TitleSelectorWrapper>
      )
    }
    return (
      <TitleSelectorWrapper isError={!!titleError}>
        <RadioButton
          onFocus={() => setFocus('title')}
          options={TitleOptions}
          id="title"
          direction="horizontal"
          alignment="center"
          defaultState={values?.title}
          error={showErrors.title ? formattedTitleError : undefined}
          valueChanged={(value) => onChange('title', value)}
          variant={determineRadioVariant({
            smallRadiosSideBySide: true
          })}
        />
      </TitleSelectorWrapper>
    )
  }

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']}>
        <AppFormQuestion
          questionKey={QuestionKey.PERSONAL_DETAILS}
          mt={3}
          mb={2}
          pr={questionCopyChangeV1 ? 7 : 0}
          textStyle={determinedFont}
        >
          {questionCopyChangeV1 ? 'And finally, let us know who you are' : 'Let us know who you are'}
        </AppFormQuestion>

        {renderOptions()}
        <>
          <MuiTextField
            onFocus={() => setFocus('firstName')}
            autoComplete="given-name"
            data-hj-suppress=""
            defaultValue={values?.firstName}
            errorMessage={showErrors.firstName ? firstNameError : undefined}
            id="firstName"
            inputMaxLength={50}
            inputType="text"
            inputVariant="outlined"
            label="First name"
            mb="34px"
            mt="12px"
            multipleInputs
            name="firstName"
            onChangeHandler={(event) => onChange('firstName', event.currentTarget.value)}
            testId="firstName"
          />
          <MuiTextField
            onFocus={() => setFocus('surname')}
            autoComplete="family-name"
            data-hj-suppress=""
            defaultValue={values?.surname}
            errorMessage={showErrors.surname ? surnameError : undefined}
            id="surname"
            inputMaxLength={50}
            inputType="text"
            inputVariant="outlined"
            label="Last name"
            mb="34px"
            multipleInputs
            name="surname"
            onChangeHandler={(event) => onChange('surname', event.currentTarget.value)}
            testId="surname"
          />
          <MuiTextField
            onFocus={() => setFocus('contactNumber')}
            autoComplete="tel"
            data-hj-suppress=""
            defaultValue={values?.contactNumber}
            errorMessage={showErrors.contactNumber ? contactNumberError : undefined}
            id="contactNumber"
            inputMaxLength={20}
            inputType="tel"
            inputVariant="outlined"
            label="Mobile number"
            mb="34px"
            multipleInputs
            name="contactNumber"
            onChangeHandler={(event) => onChange('contactNumber', event.currentTarget.value)}
            testId="contactNumber"
          />
          <MuiTextField
            onFocus={() => setFocus('emailAddress')}
            autoComplete="email"
            data-hj-suppress=""
            {...bouncerV1 ?
              { value: values.emailAddress } // Controlled component
              : { defaultValue: existingAnswers?.emailAddress }}
            errorMessage={showErrors.emailAddress ? runDisplayClickableEmail(emailAddressError) : undefined}
            id="emailAddress"
            inputMaxLength={100}
            inputType="email"
            inputVariant="outlined"
            label="Email address"
            mb="34px"
            name="emailAddress"
            onChangeHandler={(event) => onChange('emailAddress', event.currentTarget.value)}
            testId="emailInput"
            onKeyPressHandler={handleEnterPress}
          />
          {loading && <Loading />}
        </>
      </GridItem>
      <NavigationButtons
        nextHandler={nextQuestion}
        previousHandler={previousQuestion}
        disableNext={!displayNextButton}
        disablePrevious={canDisablePreviousForReviewFeature}
        nextButtonProps={{ onFocus: () => setFocus('nextButton') }}
        previousButtonProps={{ onFocus: () => setFocus('previousButton') }}
      />
    </>
  )
}
